import React, { useState, useEffect } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { UsersThree, Wallet as WalletIcon, ChartLineUp, TrendUp, Planet, Gauge, Copy, ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { formatMoney } from '../utils/formatMoney';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;

const OrbitDashboardPage = () => {
  const { language } = useLanguage();
  const { user } = useOrbitAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = (fr, en) => language === 'fr' ? fr : en;

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('orbit-token');
      const res = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setStats(res.data);
    } catch { toast.error(t('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  };

  const copyReferralLink = async () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('Lien copie !', 'Link copied!'));
    } catch { toast.info(link); }
  };

  const rankStyles = {
    mercure: { bg: 'from-gray-500 to-gray-400', label: t('Mercure', 'Mercury'), color: '#9CA3AF', emoji: '' },
    venus: { bg: 'from-yellow-500 to-amber-400', label: t('Venus', 'Venus'), color: '#FBBF24', emoji: '' },
    terre: { bg: 'from-blue-500 to-cyan-400', label: t('Terre', 'Earth'), color: '#60A5FA', emoji: '' },
    mars: { bg: 'from-red-500 to-rose-400', label: 'Mars', color: '#F87171', emoji: '' },
    jupiter: { bg: 'from-orange-500 to-amber-400', label: 'Jupiter', color: '#FB923C', emoji: '' },
    saturne: { bg: 'from-[var(--ls-gold)] to-yellow-500', label: t('Saturne', 'Saturn'), color: '#D4943A', emoji: '' },
    uranus: { bg: 'from-teal-500 to-cyan-400', label: 'Uranus', color: '#2DD4BF', emoji: '' },
  };

  const rankOrder = ['mercure', 'venus', 'terre', 'mars', 'jupiter', 'saturne', 'uranus'];
  const currentRankKey = stats?.current_rank || 'mercure';
  const currentRankIdx = rankOrder.indexOf(currentRankKey);

  const currentRank = rankStyles[stats?.current_rank] || rankStyles.mercure;
  const nextRank = stats?.next_rank ? rankStyles[stats.next_rank] : null;

  if (loading) {
    return (
      <OrbitLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[var(--ls-terracotta)] border-t-transparent rounded-full animate-spin" />
        </div>
      </OrbitLayout>
    );
  }

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">{t('Tableau de bord', 'Dashboard')}</h1>
            <p className="text-gray-300 mt-1">{t('Bienvenue', 'Welcome')}, {user?.name}</p>
          </div>
          <button onClick={copyReferralLink}
            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--ls-terracotta)]/10 border border-[var(--ls-terracotta)]/20 rounded-xl text-[var(--ls-terracotta)] hover:bg-[var(--ls-terracotta)]/20 transition-colors text-sm"
            data-testid="orbit-copy-referral">
            <Copy className="w-4 h-4" />
            <span className="truncate max-w-[200px]">{user?.referral_code}</span>
          </button>
        </div>

        {/* Rank Card */}
        <div className={`bg-gradient-to-r ${currentRank.bg} rounded-2xl p-6 shadow-xl relative overflow-hidden`}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm">{t('Rang actuel', 'Current rank')}</p>
              <h2 className="text-3xl font-bold text-white mt-1 font-heading">{currentRank.label}</h2>
              {nextRank && (
                <div className="mt-3">
                  <p className="text-white/60 text-xs mb-1">{t('Prochain rang', 'Next rank')}: {nextRank.label}</p>
                  <div className="w-48"><Progress value={stats?.rank_progress_percent || 0} className="h-2 bg-white/20" /></div>
                  <p className="text-white/60 text-xs mt-1">{Math.round(stats?.rank_progress_percent || 0)}%</p>
                </div>
              )}
            </div>
            <Planet className="w-16 h-16 text-white/30" weight="fill" />
          </div>
        </div>

        {/* ===== GROWTH ROADMAP ===== */}
        <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-6 space-y-6" data-testid="growth-roadmap">
          <h2 className="font-heading font-bold text-lg text-[var(--ls-text)]">
            {t('Croissance dans la Matrice 4x7', 'Growth in the 4x7 Matrix')}
          </h2>

          {/* Rank Progression Timeline */}
          <div>
            <p className="text-xs text-[var(--ls-text-muted)] mb-3 font-medium uppercase tracking-wider">
              {t('Progression des rangs', 'Rank Progression')}
            </p>
            <div className="relative">
              {/* Connector line */}
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-[hsl(40,20%,88%)]" />
              <div className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-[var(--ls-terracotta)] to-[var(--ls-gold)]"
                style={{ width: `${Math.max(2, ((currentRankIdx + 1) / rankOrder.length) * 100)}%` }} />

              <div className="relative flex justify-between items-start">
                {/* MOI node */}
                <div className="flex flex-col items-center z-10" style={{ width: `${100 / (rankOrder.length + 1)}%` }}>
                  <div className={`w-10 h-10 rounded-full border-3 flex items-center justify-center text-xs font-bold shadow-lg
                    bg-[var(--ls-terracotta)] text-white border-[var(--ls-terracotta)]`}>
                    {t('MOI', 'ME')}
                  </div>
                  <span className="text-[10px] font-bold text-[var(--ls-terracotta)] mt-1.5">{t('Depart', 'Start')}</span>
                </div>

                {/* Planet nodes with arrows */}
                {rankOrder.map((rank, idx) => {
                  const r = rankStyles[rank];
                  const isReached = idx <= currentRankIdx;
                  const isCurrent = idx === currentRankIdx;
                  const arrowColor = isReached ? (idx <= currentRankIdx ? 'text-[var(--ls-gold)]' : 'text-[hsl(40,20%,85%)]') : 'text-[hsl(40,20%,85%)]';
                  return (
                    <React.Fragment key={rank}>
                      {/* Arrow between nodes */}
                      <div className="flex items-center z-10 -mx-1" style={{ marginTop: '10px' }}>
                        <svg width="20" height="12" viewBox="0 0 20 12" className={arrowColor} fill="currentColor">
                          <path d="M12 0 L20 6 L12 12 L12 8 L0 8 L0 4 L12 4 Z" />
                        </svg>
                      </div>
                      {/* Planet node */}
                      <div className="flex flex-col items-center z-10" style={{ width: `${100 / (rankOrder.length + 1)}%` }}>
                        <div className={`rounded-full flex items-center justify-center font-bold shadow-md transition-all
                          ${isCurrent ? 'w-12 h-12 text-sm ring-4 ring-offset-2 scale-110' : 'w-10 h-10 text-[10px]'}
                          ${isReached ? 'text-white' : 'bg-white border-2 text-[var(--ls-text-muted)]'}`}
                          style={isReached ? {
                            backgroundColor: r.color,
                            borderColor: r.color,
                            ...(isCurrent ? { ringColor: r.color, boxShadow: `0 0 20px ${r.color}40` } : {})
                          } : { borderColor: '#E5DDD4' }}>
                          {r.label.charAt(0)}
                        </div>
                        <span className={`text-[10px] mt-1.5 font-medium text-center leading-tight
                          ${isCurrent ? 'font-bold text-[var(--ls-text)]' : isReached ? 'text-[var(--ls-text)]' : 'text-[var(--ls-text-muted)]'}`}>
                          {r.label}
                        </span>
                        {isCurrent && (
                          <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[var(--ls-terracotta)]/10 text-[var(--ls-terracotta)] font-bold mt-0.5">
                            {t('ACTUEL', 'CURRENT')}
                          </span>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 4 Directions Grid */}
          <div>
            <p className="text-xs text-[var(--ls-text-muted)] mb-3 font-medium uppercase tracking-wider">
              {t('Matrice 4 Directions x 7 Niveaux', '4 Directions x 7 Levels Matrix')}
            </p>
            <div className="flex flex-col items-center gap-1">
              {/* Nord */}
              <div className="flex items-center gap-2">
                <div className="w-16 h-8 rounded-lg bg-[var(--ls-forest)]/10 border border-[var(--ls-forest)]/25 flex items-center justify-center">
                  <span className="text-xs font-bold text-[var(--ls-forest)]">{t('NORD', 'NORTH')}</span>
                </div>
                <span className="text-[9px] text-[var(--ls-text-muted)]">7 {t('niveaux', 'levels')}</span>
              </div>

              {/* Ouest - MOI - Est */}
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-[var(--ls-text-muted)]">7 {t('niv.', 'lvl')}</span>
                  <div className="w-16 h-8 rounded-lg bg-[var(--ls-gold)]/10 border border-[var(--ls-gold)]/25 flex items-center justify-center">
                    <span className="text-xs font-bold text-[var(--ls-gold)]">{t('OUEST', 'WEST')}</span>
                  </div>
                </div>

                <div className="w-14 h-14 rounded-full bg-[var(--ls-terracotta)] flex items-center justify-center shadow-lg mx-2">
                  <span className="text-white font-bold text-sm">{t('MOI', 'ME')}</span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-16 h-8 rounded-lg bg-[var(--ls-terracotta)]/10 border border-[var(--ls-terracotta)]/25 flex items-center justify-center">
                    <span className="text-xs font-bold text-[var(--ls-terracotta)]">{t('EST', 'EAST')}</span>
                  </div>
                  <span className="text-[9px] text-[var(--ls-text-muted)]">7 {t('niv.', 'lvl')}</span>
                </div>
              </div>

              {/* Sud */}
              <div className="flex items-center gap-2">
                <div className="w-16 h-8 rounded-lg bg-blue-500/10 border border-blue-500/25 flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600">{t('SUD', 'SOUTH')}</span>
                </div>
                <span className="text-[9px] text-[var(--ls-text-muted)]">7 {t('niveaux', 'levels')}</span>
              </div>

              {/* Légende */}
              <p className="text-[10px] text-[var(--ls-text-muted)] mt-2 text-center">
                {t('4 directions x 7 niveaux de profondeur = 5 461 positions par branche = 21 844 positions au total',
                   '4 directions x 7 depth levels = 5,461 positions per branch = 21,844 total positions')}
              </p>
            </div>
          </div>
        </div>

        {/* KPIs Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('Reseau total', 'Total Downline'), value: stats?.total_downline || 0, icon: UsersThree, color: 'text-[var(--ls-forest)]' },
            { label: t('Parrainages directs', 'Direct Referrals'), value: stats?.personally_enrolled || 0, icon: TrendUp, color: 'text-[var(--ls-success)]' },
            { label: t('Matrice remplie', 'Matrix Filled'), value: `${(stats?.matrix_filled_percent || 0).toFixed(1)}%`, icon: Gauge, color: 'text-[var(--ls-gold)]' },
            { label: t('Revenus potentiels', 'Potential Earnings'), value: formatMoney(stats?.potential_earnings || 0, language), icon: ChartLineUp, color: 'text-[var(--ls-terracotta)]' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                <span className="text-xs text-[var(--ls-text-muted)]">{kpi.label}</span>
              </div>
              <p className="text-xl font-bold text-[var(--ls-text)]">{typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Earnings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: t('Commissions Matrice', 'Matrix Commissions'), value: stats?.monthly_matrix_earnings || 0, bg: 'bg-[var(--ls-forest)]/5', border: 'border-[var(--ls-forest)]/15', accent: 'text-[var(--ls-forest)]' },
            { label: 'Fast Start', value: stats?.monthly_fast_start_earnings || 0, bg: 'bg-[var(--ls-terracotta)]/5', border: 'border-[var(--ls-terracotta)]/15', accent: 'text-[var(--ls-terracotta)]' },
            { label: t('Matching Bonus', 'Matching Bonus'), value: stats?.monthly_matching_earnings || 0, bg: 'bg-[var(--ls-gold)]/5', border: 'border-[var(--ls-gold)]/15', accent: 'text-[var(--ls-gold)]' },
          ].map((item) => (
            <div key={item.label} className={`${item.bg} border ${item.border} rounded-2xl p-5`}>
              <p className={`${item.accent} text-sm font-medium`}>{item.label}</p>
              <p className="text-2xl font-bold text-white mt-2">{formatMoney(item.value, language)}</p>
              <p className="text-gray-400 text-xs mt-1">{t('Ce mois', 'This month')}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: '/dashboard/matrix', label: t('Voir ma matrice', 'View my matrix'), icon: Planet },
            { to: '/dashboard/wallet', label: t('Mon portefeuille', 'My wallet'), icon: WalletIcon },
            { to: '/dashboard/commissions', label: t('Mes commissions', 'My commissions'), icon: ChartLineUp },
          ].map((action) => (
            <Link key={action.to} to={action.to}
              className="flex items-center gap-3 p-4 bg-white border border-[hsl(40,20%,88%)] rounded-2xl hover:shadow-md hover:-translate-y-0.5 transition-all group"
              data-testid={`orbit-action-${action.to.split('/').pop()}`}>
              <action.icon className="w-5 h-5 text-[var(--ls-terracotta)]" />
              <span className="text-[var(--ls-text)] text-sm font-medium">{action.label}</span>
              <ArrowRight className="w-4 h-4 text-[var(--ls-text-muted)] ml-auto group-hover:text-[var(--ls-terracotta)] transition-colors" />
            </Link>
          ))}
        </div>

        {/* Total Earnings Card */}
        <div className="bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[var(--ls-text-muted)] text-sm">{t('Revenus totaux', 'Total Earnings')}</p>
              <p className="text-3xl font-bold text-[var(--ls-text)] mt-1">{formatMoney(stats?.total_earnings || 0, language)}</p>
            </div>
            <WalletIcon className="w-10 h-10 text-[var(--ls-terracotta)]/20" weight="fill" />
          </div>
        </div>

        {/* Strategy Documents (Admin only) */}
        {(!user?.sponsor_id) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-[var(--ls-gold)]/10 to-[var(--ls-gold)]/5 border border-[var(--ls-gold)]/20 rounded-2xl p-6">
              <p className="text-[var(--ls-gold)] font-semibold text-sm">{t('Document Strategique', 'Strategy Document')}</p>
              <p className="text-white font-bold text-lg mt-1">{t('Recompenses & Fidelite', 'Rewards & Loyalty')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('Gamification, Loyalty Pool, Tiers VIP', 'Gamification, Loyalty Pool, VIP Tiers')}</p>
              <a href={`${API}/rewards-concept/pdf?lang=${language}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[var(--ls-gold)] hover:bg-[var(--ls-gold-light)] text-white font-bold rounded-full transition-colors shadow-lg shadow-[var(--ls-gold)]/20 text-sm"
                data-testid="download-rewards-pdf">PDF</a>
            </div>
            <div className="bg-gradient-to-br from-[var(--ls-forest)]/10 to-[var(--ls-forest)]/5 border border-[var(--ls-forest)]/20 rounded-2xl p-6">
              <p className="text-[var(--ls-forest)] font-semibold text-sm">{t('Business Plan', 'Business Plan')}</p>
              <p className="text-white font-bold text-lg mt-1">LocalSite.Club</p>
              <p className="text-gray-400 text-xs mt-1">{t('Ecosysteme, Carte, Monetisation', 'Ecosystem, Card, Monetization')}</p>
              <a href={`${API}/localsite-concept/pdf?lang=${language}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[var(--ls-forest)] hover:bg-[var(--ls-forest-light)] text-white font-bold rounded-full transition-colors shadow-lg shadow-[var(--ls-forest)]/20 text-sm"
                data-testid="download-localsite-pdf">PDF</a>
            </div>
          </div>
        )}
      </div>
    </OrbitLayout>
  );
};

export default OrbitDashboardPage;
